import React, { useState } from 'react';
import {
  VictoryAxis,
  VictoryLine,
  VictoryScatter,
  VictoryGroup,
  VictoryChart,
  VictoryVoronoiContainer,
  VictoryLabel,
  VictoryArea,
} from 'victory';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useTheme } from '@material-ui/core';

import ChartTooltip from '../ChartTooltip';
import { getStrokeColor, getYAxisTicks } from '../utils';
import * as Constants from '../../../constants/charts';

const STROKE_HIGHLIGHT_WIDTH = 2;
const STROKE_NORMAL_WIDTH = 1;

const getYAxisFormatter = (yAxisObj) => (
  (item) => {
    if (item === 0) {
      return `${item}`;
    } else if (yAxisObj.paddingFactor === 1) {
      return `${item} ${yAxisObj.suffix}`;
    }
    const result = (item / yAxisObj.paddingFactor).toFixed(0);
    return `${result} ${yAxisObj.suffix}`;
  }
);

const getYAxisValues = (chartData) => {
  let yMax = -1000;
  _.each(chartData, (line) => {
    const yVal = line.type === Constants.CHART_ELEMENT_AREA ? 'high' : 'y';
    _.each(line && line.data, (item) => {
      yMax = item[yVal] > yMax ? item[yVal] : yMax;
    });
  });
  yMax = yMax === 0 ? 1 : yMax;

  return getYAxisTicks(yMax);
};

const renderAreaElement = (elementData, idx, lineColors) => {
  const { highLight, colorIndex, data, customLabelComponent, id, metric } = elementData;
  const idMetricStr = `${id}___${metric}`;
  const strokeWidth = highLight ? STROKE_HIGHLIGHT_WIDTH : STROKE_NORMAL_WIDTH;
  const scatterOpacity = (highLight || data.length === 1) ? 1 : 0;
  const upperLineName = `${idx}-area-upper-line____${idMetricStr}`;
  const upperScatterName = `${idx}-area-upper-scatter____${idMetricStr}`;
  const areaName = `${idx}-area____${idMetricStr}`;
  const lowerLineName = `${idx}-area-lower-line____${idMetricStr}`;
  const lowerScatterName = `${idx}-area-lower-scatter____${idMetricStr}`;
  return (
    <VictoryGroup
      key={`${id}-${metric}`}
    >
      {highLight && <VictoryLine
        name={upperLineName}
        y={'high'}
        data={data}
        labelComponent={ChartTooltip(customLabelComponent, '', highLight)}
        style={{
          data: {
            stroke: () => getStrokeColor(lineColors, colorIndex),
            strokeWidth: () => strokeWidth,
          },
        }}
      />}
      <VictoryScatter
        data={data}
        symbol={'circle'}
        name={upperScatterName}
        size={3}
        style={{
          data: {
            fill: getStrokeColor(lineColors, colorIndex),
            fillOpacity: () => scatterOpacity,
          },
        }}
        labels={datum => datum.y}
        labelComponent={ChartTooltip(customLabelComponent, 'highLabel', highLight)}
        y={'high'}
      />
      <VictoryArea
        data={data}
        name={areaName}
        y={'high'}
        y0={'low'}
        style={{
          data: {
            fill: getStrokeColor(lineColors, colorIndex),
            opacity: 0.2,
          },
        }}
        labelComponent={ChartTooltip(customLabelComponent, '', highLight)}
      />
      {highLight && <VictoryLine
        name={lowerLineName}
        y={'low'}
        data={data}
        labelComponent={ChartTooltip(customLabelComponent, '', highLight)}
        style={{
          data: {
            stroke: getStrokeColor(lineColors, colorIndex),
            strokeWidth: () => strokeWidth,
          },
        }}
      />}
      <VictoryScatter
        data={data}
        y={'low'}
        symbol={'circle'}
        name={lowerScatterName}
        size={3}
        style={{
          data: {
            fill: getStrokeColor(lineColors, colorIndex),
            fillOpacity: () => scatterOpacity,
          },
        }}
        labels={datum => datum.y}
        labelComponent={ChartTooltip(customLabelComponent, 'lowLabel', highLight)}
      />
    </VictoryGroup>
  );
};

const renderLineElement = (elementData, idx, lineColors, lightShadeLineColors) => {
  const { highLight, colorIndex, id, metric, useLightShadedColor } = elementData;
  const idMetricStr = `${id}___${metric}`;
  const strokeWidth = highLight ? STROKE_HIGHLIGHT_WIDTH : STROKE_NORMAL_WIDTH;
  const scatterOpacity = (highLight ||
    (elementData && elementData.data && elementData.data.length === 1)) ? 1 : 0;
  const lineName = `${idx}-line____${idMetricStr}`;
  const scatterName = `${idx}-scatter____${idMetricStr}`;
  const strokeDashArray = elementData.pattern === 'dashed' ? '4,4' : null;
  const colors = useLightShadedColor ? lightShadeLineColors : lineColors;
  return (
    <VictoryGroup
      key={`${id}-${metric}`}
    >
      <VictoryLine
        data={elementData.data}
        labelComponent={ChartTooltip()}
        name={lineName}
        style={{
          data: {
            strokeDasharray: strokeDashArray,
            stroke: getStrokeColor(colors, colorIndex),
            strokeWidth: () => strokeWidth,
          },
        }}
      />
      <VictoryScatter
        data={elementData.data}
        symbol={'circle'}
        name={scatterName}
        size={3}
        style={{
          data: {
            fill: getStrokeColor(colors, colorIndex),
            fillOpacity: () => scatterOpacity,
          },
        }}
        labels={datum => datum.y}
        labelComponent={ChartTooltip(elementData.customLabelComponent, '', highLight)}
      />
    </VictoryGroup>)
  ;
};

const renderChartElement = (elementData, idx, lineColors, lightShadeLineColors) => {
  if (elementData.type === Constants.CHART_ELEMENT_AREA) {
    return renderAreaElement(elementData, idx, lineColors);
  }
  return renderLineElement(elementData, idx, lineColors, lightShadeLineColors);
};

const CompositeXYChart = (props) => {
  const {
    chartData,
    chartScale,
    formatXAxisData,
    hoverHandler = () => {},
    lineColors,
    lightShadeLineColors,
    xAxisLabel,
    xAxisValues,
    yAxisLabel,
  } = props;

  const [activeCharts, setActiveCharts] = useState({});
  const theme= useTheme();

  const activationChangeHandler = (points, hoverHandler, activated) => {
    const idMetricMap = {};
    _.each(points, point => {
      idMetricMap[point.childName.split('____')[1]] = true;
    });
    const updatedActiveCharts = activeCharts;
    _.each(idMetricMap, (value, idMetricCombination) => {
      const [id, metric] = idMetricCombination.split('___');
      let count = updatedActiveCharts[idMetricCombination] || 0;
      if (activated) {
        count++;
        hoverHandler(id, metric, true);
      } else {
        count--;
        if (count === 0) {
          hoverHandler(id, metric, false);
        }
      }
      updatedActiveCharts[idMetricCombination] = count;
    });
    setActiveCharts(updatedActiveCharts);
  };

  const yAxisObj = getYAxisValues(chartData);
  const yAxisValues = !_.isEmpty(yAxisObj) ? yAxisObj.yTicks : [];
  const themeVariables = theme.variables || {};
  const typography = themeVariables.typography || {};
  const color = themeVariables.color || {};
  return (
    <VictoryChart
      containerComponent={
        <VictoryVoronoiContainer
          onActivated={(points) => activationChangeHandler(points, hoverHandler, true)}
          onDeactivated={(points) => activationChangeHandler(points, hoverHandler, false)}
          radius={50}
        />
      }
      width={650}
      height={250}
      scale={chartScale}
    >
      <VictoryAxis
        tickValues={xAxisValues}
        tickCount={24}
        width={900}
        tickFormat={tick => formatXAxisData(tick)}
        label={xAxisLabel}
        axisLabelComponent={
          <VictoryLabel
            dy={10}
            style={{
              color: color.bodyText,
              fontFamily: typography.bodyText,
              fontSize: 8,
            }}
          />
        }
        style={{
          ticks: { stroke: color.bodyText, size: 5 },
          tickLabels: {
            color: color.bodyText,
            fontFamily: typography.bodyText,
            fontSize: 8,
          },
        }}
        fixLabelOverlap
      />
      <VictoryAxis
        tickValues={yAxisValues}
        tickFormat={getYAxisFormatter(yAxisObj)}
        dependentAxis
        label={yAxisLabel}
        axisLabelComponent={
          <VictoryLabel
            dy={-20}
            style={{
              color: color.bodyText,
              fontFamily: typography.bodyText,
              fontSize: 8,
              paddingRight: '20px',
            }}
          />
        }
        tickLabelComponent={<VictoryLabel dy={-10} />}
        domainPadding={0}
        style={{
          ticks: { stroke: theme.color.bodyText, size: 5 },
          tickLabels: { fontSize: 8, fontFamily: typography.bodyText,
            fill: theme.color.bodyText,
          },
          grid: { stroke: theme.color.bodyText },
        }}
      />
      {chartData.map((elementData, idx) =>
        renderChartElement(elementData, idx, lineColors, lightShadeLineColors))
      }
    </VictoryChart>
  );
}

CompositeXYChart.propTypes = {
  chartData: PropTypes.array,
  chartScale: PropTypes.object,
  formatXAxisData: PropTypes.func,
  hoverHandler: PropTypes.func,
  lineColors: PropTypes.array,
  xAxisLabel: PropTypes.string,
  xAxisValues: PropTypes.array,
  lightShadeLineColors: PropTypes.array,
  yAxisLabel: PropTypes.string,
};

export default CompositeXYChart;
